import React, { useState, useEffect, useCallback } from 'react';
import { getCostoAmministrazione, saveCostoAmministrazione } from '../services/budgetCostoAmministrazioneService';

const baseSoci = ['Lorenzo', 'Giacomo', 'Francesco A.', 'Marco'];

const BudgetCostoAmministrazioneTable = ({ cantiereId, totaleRealizzoAlloggio, totaleRealizzoBox }) => {
  const [dati, setDati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modificaAttiva, setModificaAttiva] = useState(false);
  const [rows, setRows] = useState([]); // soci dinamici
  const [frenkMesi, setFrenkMesi] = useState('');
  const [frenkPercent, setFrenkPercent] = useState('');
  const [sommaTotale, setSommaTotale] = useState(0);

  const processData = useCallback((data = []) => {
    const senzaTotale = data.filter(r => r.socio !== 'TOTALE');
    setDati(senzaTotale);
    setSommaTotale(senzaTotale.reduce((sum, r) => sum + (r.totale || 0), 0));

    const frenkRow = senzaTotale.find(r => r.socio === 'Frenk');
    setFrenkMesi(frenkRow ? frenkRow.totale_mesi : '');
    const totaleVendita = (Number(totaleRealizzoAlloggio) || 0) + (Number(totaleRealizzoBox) || 0);
    setFrenkPercent(
      frenkRow ? (frenkRow.totale / totaleVendita) * 100 : ''
    );

    const senzaFrenk = senzaTotale.filter(r => r.socio !== 'Frenk');

    const baseRows = baseSoci.map(nome => {
      const esistente = senzaFrenk.find(r => r.socio === nome);
      return esistente
        ? { socio: nome, importo: esistente.importo, totale_mesi: esistente.totale_mesi }
        : { socio: nome, importo: '', totale_mesi: '' };
    });

    const extraRows = senzaFrenk
      .filter(r => !baseSoci.includes(r.socio))
      .map(r => ({ socio: r.socio, importo: r.importo, totale_mesi: r.totale_mesi }));

    setRows([...baseRows, ...extraRows]);
  }, [totaleRealizzoAlloggio, totaleRealizzoBox]);

  useEffect(() => {
    const fetchData = async () => {
      if (!cantiereId) return;
      try {
        const res = await getCostoAmministrazione(cantiereId);
        if (res.success) {
          processData(res.data || []);
        }
      } catch (err) {
        console.error('Errore nel recupero costi amministrazione:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cantiereId, processData]);

  const handleRowChange = (index, field, value) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => {
    setRows(prev => [...prev, { socio: '', importo: '', totale_mesi: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const voci = rows
        .filter(r => r.socio)
        .map(r => {
          const importo = parseFloat(r.importo || 0);
          const mesi = parseInt(r.totale_mesi || 0);
          return {
            socio: r.socio,
            importo,
            totale_mesi: mesi,
            totale: importo * mesi,
          };
        });

      const totaleVendita = (Number(totaleRealizzoAlloggio) || 0) + (Number(totaleRealizzoBox) || 0);
      const mesiFrenk = parseInt(frenkMesi || 0);
      const percFrenk = parseFloat(frenkPercent || 0);
      const totaleFrenk = totaleVendita * (percFrenk / 100);
      const importoFrenk = mesiFrenk ? totaleFrenk / mesiFrenk : 0;
      voci.push({ socio: 'Frenk', importo: importoFrenk, totale_mesi: mesiFrenk, totale: totaleFrenk });

      await saveCostoAmministrazione(cantiereId, voci);
      const refreshed = await getCostoAmministrazione(cantiereId);
      processData(refreshed.data || []);
      setModificaAttiva(false);
    } catch (error) {
      console.error('Errore salvataggio costi amministrazione:', error);
      alert('Errore durante il salvataggio');
    }
  };

  if (loading) {
    return <p>Caricamento costi amministrazione...</p>;
  }

  if (dati.length === 0 || modificaAttiva) {
    const totaleVendita = (Number(totaleRealizzoAlloggio) || 0) + (Number(totaleRealizzoBox) || 0);
    const mesiFrenk = parseInt(frenkMesi || 0);
    const percFrenk = parseFloat(frenkPercent || 0);
    const totaleFrenk = totaleVendita * (percFrenk / 100);
    const importoFrenk = mesiFrenk ? totaleFrenk / mesiFrenk : 0;

    return (
      <div className="card p-4">
        <h5>Inserisci Costi Amministrazione</h5>
        <form onSubmit={handleSubmit}>
          {rows.map((row, idx) => (
            <div className="row mb-3" key={idx}>
              <div className="col-md-3">
                <label className="form-label">Socio</label>
                <input
                  type="text"
                  className="form-control"
                  value={row.socio}
                  onChange={e => handleRowChange(idx, 'socio', e.target.value)}
                  disabled={baseSoci.includes(row.socio)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Importo</label>
                <input
                  type="number"
                  className="form-control"
                  value={row.importo}
                  onChange={e => handleRowChange(idx, 'importo', e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Tot. mesi</label>
                <input
                  type="number"
                  className="form-control"
                  value={row.totale_mesi}
                  onChange={e => handleRowChange(idx, 'totale_mesi', e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Totale</label>
                <input
                  type="number"
                  className="form-control"
                  value={(Number(row.importo) || 0) * (Number(row.totale_mesi) || 0)}
                  readOnly
                />
              </div>
            </div>
          ))}

          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label">Socio</label>
              <input type="text" className="form-control" value="Frenk" disabled />
            </div>
            <div className="col-md-2">
              <label className="form-label">Percentuale</label>
              <input
                type="number"
                className="form-control"
                value={frenkPercent}
                onChange={e => setFrenkPercent(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Tot. mesi</label>
              <input
                type="number"
                className="form-control"
                value={frenkMesi}
                onChange={e => setFrenkMesi(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Importo</label>
              <input type="number" className="form-control" value={importoFrenk.toFixed(2)} readOnly />
            </div>
            <div className="col-md-3">
              <label className="form-label">Totale</label>
              <input type="number" className="form-control" value={totaleFrenk.toFixed(2)} readOnly />
            </div>
          </div>

          <button type="button" className="btn btn-secondary me-3" onClick={addRow}>
            Aggiungi socio
          </button>
          <button type="submit" className="btn btn-primary">Salva</button>
        </form>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h5>Costo Amministrazione</h5>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>SOCIO</th>
              <th>IMPORTO</th>
              <th>TOT. MESI</th>
              <th>TOTALE</th>
            </tr>
          </thead>
          <tbody>
            {dati.map((r, idx) => (
              <tr key={idx}>
                <td>{r.socio}</td>
                <td>{new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(r.importo || 0)}</td>
                <td>{r.totale_mesi}</td>
                <td>{new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(r.totale || 0)}</td>
              </tr>
            ))}
            <tr>
              <td>TOTALE</td>
              <td></td>
              <td></td>
              <td>{new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(sommaTotale)}</td>
            </tr>
          </tbody>
        </table>
        <div className="text-end mt-3">
          <button className="btn btn-warning" onClick={() => setModificaAttiva(true)}>
            ✏️ Modifica Dati
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetCostoAmministrazioneTable;
